<script lang="ts">
    import type { LanguageDataCarrierV2 } from "@app/models/api";
    import {
        dictionary,
        updateDictionary,
    } from "@app/services/currentDictionary.service";
    import { getLanguages } from "@app/services/glosbe-dictionary-api/get-languages";
    import { debounce } from "@app/utils/debounce";
    import {
        ItemsListManager,
        ItemsListManagerStateCreator,
        setItemsListManager,
    } from "../../ItemsList/ItemsListManager";
    import ItemsListInput from "../../ItemsList/ItemsListInput.svelte";
    import LanguagePickerListItems from "../../Languages/LanguagePickerListItems.svelte";
    import MobileScreen from "@app/components/MobileScreen.svelte";
    import type { DictionaryLanguageType } from "../ChangeDictionary.store";
    import {
        changeLanguageWidgetState,
        hideChangeLanguageWidget,
    } from "../ChangeDictionary.store";
    import LazyAppearingComponent from "@app/components/LazyAppearingComponent.svelte";
    import { getMessage } from "@app/services/i18n.service";
    import {
        PAGE_LANGUAGE_ALL__search__placeholder,
        type I18NMessage,
    } from "@app/services/i18n.messages";
    import {
        COMMON__allOtherLanguages__title,
        COMMON__popular,
        COMMON__recentlyUsedLanguages,
        SEARCH__translateFrom,
        SEARCH__translateInto,
    } from "@app/services/i18n.messages";

    export let languageType: DictionaryLanguageType = "source";

    const manager = new ItemsListManager();
    setItemsListManager(manager);

    async function updateLanguagesList(query: string) {
        manager.setInProgress(true);

        let languages = await getLanguages({ query });

        const listItemsCreator = new ItemsListManagerStateCreator();
        if (languages && languages.recentLanguages && languages.popularLanguages && languages.otherLanguages) {
            if (languages.recentLanguages.length > 0) {
                listItemsCreator.addItems(
                    getMessage(COMMON__recentlyUsedLanguages),
                    languages.recentLanguages
                );
            }
            if (languages.popularLanguages.length > 0) {
                listItemsCreator.addItems(
                    getMessage(COMMON__popular),
                    languages.popularLanguages
                );
            }
            if (languages.otherLanguages.length > 0) {
                listItemsCreator.addItems(
                    getMessage(COMMON__allOtherLanguages__title),
                    languages.otherLanguages
                );
            }
        }
        manager.setInProgress(false);
        manager.setState(listItemsCreator.state);
    }

    async function onInput(event: Event) {
        event.preventDefault();
        const query = (event.target as HTMLInputElement).value;
        updateLanguagesList(query);
    }

    manager.onItemSelected((selectedLanguage: unknown) => {
        let { l1, l2 } = $dictionary;
        if (languageType === "source") {
            l1 = selectedLanguage as LanguageDataCarrierV2;
        } else {
            l2 = selectedLanguage as LanguageDataCarrierV2;
        }
        updateDictionary({ l1, l2 });
        hideChangeLanguageWidget();
    });

    let message: I18NMessage;
    $: {
        if (
            $changeLanguageWidgetState.open &&
            languageType !== $changeLanguageWidgetState.type
        ) {
            updateLanguagesList("");
            languageType = $changeLanguageWidgetState.type || "source";
            message =
                languageType === "source"
                    ? SEARCH__translateFrom
                    : SEARCH__translateInto;
        }
    }
</script>

<LazyAppearingComponent
    show={$changeLanguageWidgetState.open}
    class="fixed inset-0 bg-white transition-all duration-300 ease-in-out w-full h-full z-80 text-gray-900"
    hiddenClass="dir-aware-left-100p"
    dataElement="change-language-widget"
>
    <MobileScreen {message} onBackClick={hideChangeLanguageWidget}>
        <div slot="navbar" class="bg-white rounded p-1 mx-2 flex-1">
            <ItemsListInput
                onInput={debounce(150, onInput)}
                autoFocus={true}
                ariaLabel="Search language"
                placeholder={getMessage(PAGE_LANGUAGE_ALL__search__placeholder)}
                classNames="text-slate-800 bg-slate-200 rounded focus:outline-none text-base w-full h-full p-2 w-full"
            />
        </div>
        <div class="flex-1 relative h-full px-2">
            <LanguagePickerListItems />
        </div>
    </MobileScreen>
</LazyAppearingComponent>

<style>
    :global(.z-80) {
        z-index: 80;
    }
</style>
